<template>
  <main class="ws">

<!-- ============ 首屏 ============ -->
<section class="ph ph--gray ph--center">
  <div class="wrap">
    <span class="ph__kicker">{{ $t("价格") }}</span>
    <h1 class="ph__title">{{ $t("订阅按店铺收费") }}<br>{{ $t("佣金按成交额往下走") }}</h1>
    <p class="ph__sub">{{ $t("套餐是固定的工具费，佣金只在你出单时收。") }}<b>{{ $t("卖得越多，费率越低") }}</b>{{ $t("成交额越大费率越低，自动算，不用谈。") }}</p>
    <div class="btns" style="justify-content:center">
      <router-link class="btn btn--primary" to="/sign-up">{{ $t("免费试用") }} {{ trialDays }} {{ $t("天") }}</router-link>
      <a class="btn btn--line" href="#compare">{{ $t("逐项对比") }}</a>
    </div>
    <p class="ph__meta">
      <i><svg class="ico"><use href="#i-check"/></svg>{{ $t("试用不绑卡") }}</i>
      <i><svg class="ico"><use href="#i-check"/></svg>{{ $t("月 / 季 / 年随时换") }}</i>
      <i><svg class="ico"><use href="#i-check"/></svg>{{ $t("佣金封顶") }} {{ minRate }}%</i>
    </p>

    <div class="guide guide--center">
      <img class="guide__m" src="/website/img/tao-think.png" alt="">
      <div class="bubble">{{ $t("先领 {days} 天试用，跑通一单再选档。", { days: trialDays }) }}<b>{{ $t("佣金只有成交才收") }}</b>{{ $t("，没出单一分不收。") }}</div>
    </div>
  </div>
</section>

<!-- ============ 套餐 ============ -->
<section class="sec">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("订阅套餐") }}</span>
      <h2 class="sec__title">{{ $t("四档，按你开到第几家店来选") }}</h2>
      <p class="sec__sub">{{ $t("价格以美元计，月付 / 季付 / 年付三档同权，只是单价不同。") }}</p>
    </div>

    <div class="plans plans--4">
      <div class="plan" v-for="p in pkgs" :key="p.package_key" :class="{ 'plan--hot': p.is_hot }">
        <span class="plan__badge" v-if="p.is_hot">{{ $t("多数人选这个") }}</span>
        <div class="plan__name">{{ pkgName(p) }}</div>
        <p class="plan__desc">{{ pkgDesc(p) }}</p>
        <div class="plan__price">
          <b>${{ p.is_trial ? 0 : p.price.month }}</b>
          <span v-if="p.is_trial">/ {{ p.trial_days }} {{ $t("天") }}</span>
          <span v-else>{{ $t("/ 月") }}</span>
        </div>
        <p class="plan__unit" v-if="p.is_trial">{{ $t("每店限领一次，不绑卡") }}</p>
        <p class="plan__unit" v-else>{{ $t("季付") }} ${{ p.price.quarter }} · {{ $t("年付") }} ${{ p.price.year }}</p>
        <router-link class="plan__btn" to="/sign-up">{{ p.is_trial ? $t("领试用") : $t("选这个") }}</router-link>
        <ul>
          <li v-for="(q, qi) in quotaLines(p)" :key="qi">
            <svg class="ico"><use href="#i-check"/></svg>
            <span v-if="qi === 0"><b>{{ num(q.n) }} {{ $t(q.unit) }}</b></span>
            <span v-else>{{ num(q.n) }} {{ $t(q.unit) }}</span>
          </li>
        </ul>
      </div>
    </div>
    <p class="plans__note">{{ $t("价格取自平台计费中心，调整后本页同步。季付按 3 个月、年付按 12 个月计价，中途换档按剩余天数折算。") }}</p>
  </div>
</section>

<!-- ============ 佣金阶梯 ============ -->
<section class="sec sec--ink" id="commission">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("交易佣金") }}</span>
      <h2 class="sec__title">{{ $t("成交额越大，抽成越低") }}</h2>
      <p class="sec__sub">{{ $t("按店铺自然月成交额（折算美元）命中档位，整月按该档比例计提。没成交就没有佣金。") }}</p>
    </div>

    <div class="ladder">
      <div
        class="ladder__row"
        v-for="(t, ti) in tiers"
        :key="ti"
        :class="{ 'is-best': ti === tiers.length - 1 }"
      >
        <div class="ladder__range">{{ tierLabel(t) }}</div>
        <div class="ladder__bar"><i :style="{ width: barWidth(t) + '%' }"></i></div>
        <div class="ladder__rate">{{ t.rate }}<em>%</em></div>
      </div>
    </div>

    <div class="ladder__notes">
      <p><svg class="ico"><use href="#i-check"/></svg>{{ $t("只对已完成且未退款的订单计提，退款自动冲回。") }}</p>
      <p><svg class="ico"><use href="#i-check"/></svg>{{ $t("佣金与套餐档位无关，选哪档都是同一套阶梯。") }}</p>
      <p><svg class="ico"><use href="#i-check"/></svg>{{ $t("账单每月 1 号出，后台可下载明细，逐单可查。") }}</p>
    </div>
  </div>
</section>

<!-- ============ 对比表 ============ -->
<section class="sec sec--gray" id="compare">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("逐项对比") }}</span>
      <h2 class="sec__title">{{ $t("差在哪，一条条看") }}</h2>
    </div>

    <div class="tbl-wrap">
      <table class="tbl">
        <thead>
          <tr>
            <th>{{ $t("额度") }}</th>
            <th v-for="p in pkgs" :key="p.package_key">{{ pkgName(p) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ $t("月付价格") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ p.is_trial ? $t("免费") : '$' + p.price.month }}</td>
          </tr>
          <tr>
            <td>{{ $t("季付价格") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ p.is_trial ? '—' : '$' + p.price.quarter }}</td>
          </tr>
          <tr>
            <td>{{ $t("年付价格") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ p.is_trial ? '—' : '$' + p.price.year }}</td>
          </tr>
          <tr>
            <td>{{ $t("可开店数") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ num(p.quota.shops) }}</td>
          </tr>
          <tr>
            <td>{{ $t("可上架商品") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ num(p.quota.goods) }}</td>
          </tr>
          <tr>
            <td>{{ $t("文本翻译") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ num(p.quota.textTrans) }} {{ $t("次") }}</td>
          </tr>
          <tr>
            <td>{{ $t("图片翻译") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ num(p.quota.imgTrans) }} {{ $t("次") }}</td>
          </tr>
          <tr>
            <td>{{ $t("AI 模板") }}</td>
            <td v-for="p in pkgs" :key="p.package_key">{{ num(p.quota.aiThemes) }} {{ $t("套") }}</td>
          </tr>
          <tr>
            <td>{{ $t("交易佣金") }}</td>
            <td :colspan="pkgs.length">{{ maxRate }}% → {{ minRate }}% {{ $t("阶梯，与档位无关") }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="cmp__note">{{ $t("额度按月计，用完可等次月重置，或升档补足；未用完不结转到下月。") }}</p>
  </div>
</section>

<!-- ============ FAQ ============ -->
<section class="sec">
  <div class="wrap">
    <div class="sec__head">
      <span class="eyebrow">{{ $t("常见问题") }}</span>
      <h2 class="sec__title">{{ $t("付钱之前，先把这些问清楚") }}</h2>
    </div>

    <div class="faq" style="max-width:860px;margin:0 auto">
      <details open>
        <summary>{{ $t("佣金到底怎么算？") }}</summary>
        <div class="faq__a">{{ $t("按店铺当月成交额（折算美元）落在哪一档，整月就按那一档的比例计提，档位和费率见上面的阶梯表。退款订单会把对应佣金冲回，不多收。") }}</div>
      </details>
      <details>
        <summary>{{ $t("佣金是按订单金额还是按利润收？") }}</summary>
        <div class="faq__a">{{ $t("按订单实际成交金额（不含运费和税），不是按你的利润。所以亏钱卖的单也会产生佣金——定价时把这一项算进成本，后台的毛利计算器已经默认扣掉了。") }}</div>
      </details>
      <details>
        <summary>{{ $t("试用结束后会怎样？") }}</summary>
        <div class="faq__a">{{ $t("试用期不绑卡、不自动扣费。到期后店铺进入只读状态：商品和订单都还在，但不能接新单，选一档套餐付费后立即恢复。每个店铺只能领一次试用。") }}</div>
      </details>
      <details>
        <summary>{{ $t("可以随时换档或退款吗？") }}</summary>
        <div class="faq__a">{{ $t("可以。升档立即生效，按剩余天数折算差价；降档在下个账期生效，不会立刻断掉额度。月付随时取消，年付 7 天内未使用可全额退。") }}</div>
      </details>
      <details>
        <summary>{{ $t("换套餐，店铺数据会丢吗？") }}</summary>
        <div class="faq__a">{{ $t("不会。商品、订单、客户、模板都会保留。降档后超出新额度的部分变成只读，不会删除；再升回来立刻恢复。") }}</div>
      </details>
      <details>
        <summary>{{ $t("多开店铺怎么算？") }}</summary>
        <div class="faq__a">{{ $t("按套餐给的开店数算，档位越高能开的店越多。每家店独立域名、独立语言、独立客户数据，佣金也按店分别计算档位。") }}</div>
      </details>
    </div>
  </div>
</section>

<!-- ============ 提示与 CTA ============ -->

<section class="sec sec--tight">
  <div class="wrap">
    <div class="pandy">
      <img class="pandy__m" src="/website/img/tao-think.png" alt="">
      <div>
        <p class="pandy__t">{{ $t("先别挑最贵的") }}</p>
        <p class="pandy__b">{{ $t("大部分人是") }}<b>{{ $t("先领 {days} 天试用，再选单店版跑一个月", { days: trialDays }) }}</b>{{ $t("，等单量稳定了才升档。升档按天折算，不用重新付一整月。") }}</p>

      </div>
    </div>
  </div>
</section>

<section class="sec sec--tight">
  <div class="wrap">
    <div class="cta">
      <div>
        <h2 class="cta__t">{{ $t("先试 {days} 天，跑通一单再说", { days: trialDays }) }}</h2>
        <p class="cta__d">{{ $t("不绑卡、不自动续费。试用到期店铺只读，数据不会丢。") }}</p>
      </div>
      <div class="cta__btns">
        <router-link class="btn btn--primary" to="/sign-up">{{ $t("领 {days} 天试用", { days: trialDays }) }}</router-link>
        <router-link class="btn btn--ghost" to="/question">{{ $t("还有问题") }}</router-link>
      </div>
    </div>
  </div>
</section>

</main>
</template>

<script>
/**
 * 价格页
 * ------------------------------------------------------------------
 * 套餐价目与佣金阶梯都从后台读（/Admin/Shopsubscribe/get_public_pricing）：
 * 改价、调额度、开关某个套餐只在后台维护一份，官网刷新即同步，不用再来改代码。
 *
 * fallback 只在接口不可用时兜底（后端没部署、网络问题），值取当前后台价目；
 * 接口一旦返回就以接口为准。
 */
const FALLBACK = {
  currency: 'USD',
  trial_days: 14,
  packages: [
    {
      package_key: 'trial', name: '试用版', desc: '先免费用 14 天，把店开起来看效果',
      price: { trial: 0, month: 0, quarter: 0, year: 0 },
      quota: { shops: 1, goods: 100, textTrans: 100, imgTrans: 20, aiThemes: 3 },
      is_trial: 1, trial_days: 14, is_hot: 0,
    },
    {
      package_key: 'basic', name: '单店版', desc: '一个人做代购，够用就好',
      price: { trial: 0, month: 9, quarter: 24, year: 72 },
      quota: { shops: 1, goods: 300, textTrans: 200, imgTrans: 30, aiThemes: 3 },
      is_trial: 0, is_hot: 0,
    },
    {
      package_key: 'standard', name: '起步版', desc: '有团队之后的第一档',
      price: { trial: 0, month: 19, quarter: 51, year: 180 },
      quota: { shops: 1, goods: 2000, textTrans: 3000, imgTrans: 300, aiThemes: 20 },
      is_trial: 0, is_hot: 1,
    },
    {
      package_key: 'pro', name: '成长版', desc: '多店、批量铺货、要账期',
      price: { trial: 0, month: 49, quarter: 132, year: 470 },
      quota: { shops: 10, goods: 30000, textTrans: 50000, imgTrans: 5000, aiThemes: 200 },
      is_trial: 0, is_hot: 0,
    },
  ],
  tiers: [
    { min_amount: 0, max_amount: 1500, rate: 6 },
    { min_amount: 1500, max_amount: 7000, rate: 5 },
    { min_amount: 7000, max_amount: 28000, rate: 4 },
    { min_amount: 28000, max_amount: 0, rate: 3 },
  ],
}

export default {
  name: 'WebsitePricing',
  data () {
    return {
      remote: null,
    }
  },
  computed: {
    source () {
      const d = this.remote
      if (d && Array.isArray(d.packages) && d.packages.length) return d
      return FALLBACK
    },
    pkgs () {
      return this.source.packages
    },
    tiers () {
      return Array.isArray(this.source.tiers) ? this.source.tiers : []
    },
    trialDays () {
      return Number(this.source.trial_days) || 14
    },
    maxRate () {
      return this.tiers.length ? Math.max.apply(null, this.tiers.map(t => Number(t.rate) || 0)) : 0
    },
    minRate () {
      return this.tiers.length ? Math.min.apply(null, this.tiers.map(t => Number(t.rate) || 0)) : 0
    },
  },
  created () {
    this.fetchPricing()
  },
  methods: {
    fetchPricing () {
      this.$api.public.getPublicPricing({}).then(res => {
        if (res && Number(res.status) === 1 && res.data && Array.isArray(res.data.packages) && res.data.packages.length) {
          this.remote = res.data
        }
      }).catch(() => {})
    },
    /** 千分位：3000 → 3,000（额度数字很小，固定 en-US 分组避免各语种分隔符不一致） */
    /**
     * 套餐名/描述按 package_key 映射到本地文案：
     * 后台改的是中文名，直接 $t(p.name) 在 19 个语种里会掉回中文；
     * 新增了没登记过的 key 才退回后台原文（此时显示中文，属可接受降级）。
     */
    pkgName (p) {
      const map = { trial: '试用版', basic: '单店版', standard: '起步版', pro: '成长版' }
      return this.$t(map[p.package_key] || p.name)
    },
    pkgDesc (p) {
      const map = {
        trial: '先免费用 14 天，把店开起来看效果',
        basic: '一个人做代购，够用就好',
        standard: '有团队之后的第一档',
        pro: '多店、批量铺货、要账期',
      }
      return this.$t(map[p.package_key] || p.desc)
    },
    num (n) {
      return Number(n || 0).toLocaleString('en-US')
    },
    quotaLines (p) {
      const q = p.quota || {}
      return [
        { n: q.shops, unit: '个店铺' },
        { n: q.goods, unit: '个商品' },
        { n: q.textTrans, unit: '次文本翻译' },
        { n: q.imgTrans, unit: '次图片翻译' },
        { n: q.aiThemes, unit: '套 AI 模板' },
      ]
    },
    /** 阶梯文案：末档没有上限，首档没有下限 */
    tierLabel (t) {
      const min = Number(t.min_amount) || 0
      const max = Number(t.max_amount) || 0
      if (max <= 0) return '$' + this.num(min) + ' ' + this.$t('以上')
      if (min <= 0) return this.$t('月成交额 ＜') + ' $' + this.num(max)
      return '$' + this.num(min) + ' – $' + this.num(max)
    },
    /** 条形长度按费率高低的相对比例，最高档 100% */
    barWidth (t) {
      const max = this.maxRate || 1
      return Math.max(8, Math.round((Number(t.rate) || 0) / max * 100))
    },
  },
}
</script>
